import React, { useState, useEffect, useContext, useCallback } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend, Cell,
} from 'recharts';
import { AlertTriangle, Clock, CheckCircle2, ListChecks, TrendingUp, AlertCircle, RefreshCw, CalendarDays, Download } from 'lucide-react';

const GREEN = '#5FA718'; const BLUE = '#2F6DB5'; const NAVY = '#0E2A47';
const PRIORITY_COLORS = { P1: '#DC2626', P2: '#EA580C', P3: '#D97706', P4: '#2F6DB5', P5: '#64748B', '—': '#94A3B8' };
const iso = (d) => d.toISOString().slice(0, 10);
const presetRange = (key) => {
  const end = new Date(); const start = new Date();
  if (key === 'last30') start.setDate(end.getDate() - 30);
  else if (key === 'week') start.setDate(end.getDate() - end.getDay());
  else if (key === 'month') start.setDate(1);
  else if (key === 'quarter') { const q = Math.floor(end.getMonth() / 3); start.setMonth(q * 3, 1); }
  return { start: iso(start), end: iso(end) };
};

const parseISO = (s) => { if (!s) return undefined; const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const fmtISO = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
const fmtLabel = (s) => (s ? (_fmtDate(s) || 'Pick a date') : 'Pick a date');

const DateField = ({ value, onChange, disabledMatcher, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testid}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-[#8FC93A] focus:outline-none focus:ring-2 focus:ring-[#8FC93A]/20">
          <CalendarDays size={15} className="text-slate-400" />
          {fmtLabel(value)}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={parseISO(value)} defaultMonth={parseISO(value)} disabled={disabledMatcher}
          onSelect={(dt) => { if (dt) { onChange(fmtISO(dt)); setOpen(false); } }} initialFocus />
      </PopoverContent>
    </Popover>
  );
};

const Kpi = ({ label, value, tone = 'text-[#0E2A47]', icon: Icon, sub, testid, onClick }) => (
  <button onClick={onClick} disabled={!onClick} data-testid={testid}
    className={`rounded-xl border border-slate-200 bg-white p-4 text-left transition-all ${onClick ? 'hover:border-[#8FC93A] hover:shadow-sm cursor-pointer' : ''}`}>
    <div className="flex items-center justify-between">
      <div className={`text-3xl font-extrabold tracking-tight ${value ? tone : 'text-slate-300'}`}>{value ?? 0}{sub ? <span className="text-base font-semibold text-slate-400"> {sub}</span> : ''}</div>
      {Icon && <Icon size={18} className="text-slate-300" />}
    </div>
    <div className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</div>
  </button>
);

const ChartCard = ({ title, children, testid }) => (
  <Card data-testid={testid} className="border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5">
    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">{title}</h3>
    <div style={{ width: '100%', height: 240 }}>{children}</div>
  </CardContent></Card>
);

const TRADES = ['Janitorial', 'Event Support', 'Landscape', 'Security', 'Sweeping', 'General Maintenance', 'Special Projects', 'HVAC', 'Plumbing', 'Electrical'];
const selCls = 'rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:border-[#8FC93A] focus:outline-none focus:ring-2 focus:ring-[#8FC93A]/20';

const ClientReports = ({ onDrill }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [preset, setPreset] = useState('last30');
  const [range, setRange] = useState(presetRange('last30'));
  const [locationId, setLocationId] = useState('');
  const [tradeType, setTradeType] = useState('');
  const [priority, setPriority] = useState('');
  const [data, setData] = useState(null);
  const [locationOptions, setLocationOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setError(false);
    const params = { start: range.start, end: range.end };
    if (locationId) params.location_id = locationId;
    if (tradeType) params.trade_type = tradeType;
    if (priority) params.priority_level = priority;
    axios.get(`${API}/reports/client-overview`, { ...authHeaders, params }).then((r) => {
      setData(r.data);
      if (!locationId && !tradeType && !priority && (r.data?.by_location || []).length) {
        setLocationOptions(r.data.by_location.filter((l) => l.location_id));
      }
    }).catch(() => setError(true)).finally(() => setLoading(false));
  }, [range, locationId, tradeType, priority]);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (key) => { setPreset(key); setRange(presetRange(key)); };
  const activeLocationName = locationId ? (locationOptions.find((l) => l.location_id === locationId)?.location_name) : undefined;
  const drill = (extra) => onDrill?.({
    start: range.start, end: range.end,
    location_id: locationId || undefined, location_name: activeLocationName,
    trade_type: tradeType || undefined, priority: priority || undefined,
    ...extra,
  });
  const statusData = data ? Object.entries(data.by_status).map(([status, count]) => ({ status, count })) : [];
  const priorityData = data ? Object.entries(data.by_priority).map(([p, count]) => ({ priority: p, count })) : [];
  const ct = data?.completion_times || {};
  const ov = data?.overdue || {};
  const PRESETS = [['last30', 'Last 30 Days'], ['week', 'This Week'], ['month', 'This Month'], ['quarter', 'This Quarter']];
  const noResults = data && (data?.totals?.total || 0) === 0;

  const downloadSummary = () => {
    const params = new URLSearchParams({ auth: token });
    if (range.start) params.set('start', range.start);
    if (range.end) params.set('end', range.end);
    if (locationId) params.set('location_id', locationId);
    if (activeLocationName) params.set('location_name', activeLocationName);
    if (tradeType) params.set('trade_type', tradeType);
    if (priority) params.set('priority_level', priority);
    window.open(`${API}/reports/client-summary.pdf?${params.toString()}`, '_blank');
  };

  const exportHistory = (fmt) => {
    const params = new URLSearchParams({ auth: token, format: fmt });
    if (range.start) params.set('start', range.start);
    if (range.end) params.set('end', range.end);
    if (locationId) params.set('location_id', locationId);
    if (tradeType) params.set('trade_type', tradeType);
    if (priority) params.set('priority_level', priority);
    window.open(`${API}/reports/client-history-export?${params.toString()}`, '_blank');
  };

  const costs = data?.costs || {};
  const fmtMoney = (v) => (typeof v === 'number' ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00');

  return (
    <div data-testid="client-reports">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Facilities Director</div>
          <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0E2A47]">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Service performance across your portfolio — your locations only.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => exportHistory('csv')} data-testid="client-export-csv-btn"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 h-12 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50 shrink-0">
            <Download size={16} /> CSV
          </button>
          <button onClick={() => exportHistory('xlsx')} data-testid="client-export-xlsx-btn"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 h-12 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50 shrink-0">
            <Download size={16} /> Excel
          </button>
          <button onClick={downloadSummary} data-testid="client-reports-download-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-[#0E2A47] px-5 h-12 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#16324F] shrink-0">
            <Download size={18} /> Download Summary Report
          </button>
        </div>
      </div>

      <Card className="mb-6 border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-4 flex flex-wrap items-end gap-3" data-testid="client-reports-filters">
        <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-0.5">
          {PRESETS.map(([k, lbl]) => (
            <button key={k} onClick={() => applyPreset(k)} data-testid={`client-preset-${k}`}
              className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${preset === k ? 'bg-[#0E2A47] text-white shadow-sm' : 'text-slate-600 hover:text-[#0E2A47]'}`}>{lbl}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <DateField testid="client-reports-start" value={range.start}
            disabledMatcher={range.end ? { after: parseISO(range.end) } : undefined}
            onChange={(v) => { setPreset('custom'); setRange((p) => ({ start: v, end: p.end && v > p.end ? v : p.end })); }} />
          <span className="text-slate-400 text-sm">→</span>
          <DateField testid="client-reports-end" value={range.end}
            disabledMatcher={range.start ? { before: parseISO(range.start) } : undefined}
            onChange={(v) => { setPreset('custom'); setRange((p) => ({ ...p, end: v })); }} />
        </div>
        <select value={locationId} onChange={(e) => setLocationId(e.target.value)} data-testid="client-reports-location" className={selCls}>
          <option value="">All my locations</option>
          {locationOptions.map((l) => <option key={l.location_id} value={l.location_id}>{l.location_name}</option>)}
        </select>
        <select value={tradeType} onChange={(e) => setTradeType(e.target.value)} data-testid="client-reports-trade" className={selCls}>
          <option value="">All trades</option>
          {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="client-reports-priority" className={selCls}>
          <option value="">All priorities</option>
          {['P1', 'P2', 'P3', 'P4', 'P5'].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </CardContent></Card>

      {error ? (
        <Card className="border-slate-200 shadow-sm rounded-2xl"><CardContent className="py-16 text-center" data-testid="client-reports-error">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-400"><AlertCircle size={26} /></div>
          <p className="text-sm text-slate-500 mb-4">We couldn't load your reports.</p>
          <button onClick={load} data-testid="client-reports-retry" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"><RefreshCw size={14} /> Try again</button>
        </CardContent></Card>
      ) : loading && !data ? (
        <div data-testid="client-reports-loading" className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-slate-100 animate-pulse" />)}</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-72 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6" data-testid="client-reports-kpis">
            <Kpi testid="client-kpi-total" label="Total (range)" value={data?.totals?.total} icon={ListChecks} onClick={() => drill({})} />
            <Kpi testid="client-kpi-open" label="Open" value={data?.totals?.active} tone="text-[#2F6DB5]" icon={Clock} onClick={() => drill({ special: 'active' })} />
            <Kpi testid="client-kpi-completed" label="Completed" value={data?.totals?.completed} tone="text-[#4f8d14]" icon={CheckCircle2} onClick={() => drill({ special: 'completed' })} />
            <Kpi testid="client-kpi-overdue" label="Overdue" value={ov.total} tone="text-red-600" icon={AlertTriangle} onClick={() => drill({ special: 'overdue' })} />
            <Kpi testid="client-kpi-avg-received" label="Avg Recv→Done" value={ct.avg_received_to_completed_days ?? '—'} sub={ct.avg_received_to_completed_days != null ? 'd' : ''} icon={TrendingUp} />
            <Kpi testid="client-kpi-avg-scheduled" label="Avg Sched→Done" value={ct.avg_scheduled_to_completed_days ?? '—'} sub={ct.avg_scheduled_to_completed_days != null ? 'd' : ''} icon={TrendingUp} />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="client-cost-kpis">
            <Kpi testid="client-cost-service" label="Service-Call Total" value={fmtMoney(costs.service_call)} icon={ListChecks} />
            <Kpi testid="client-cost-materials" label="Materials & Supplies" value={fmtMoney(costs.materials)} icon={ListChecks} />
            <Kpi testid="client-cost-additional" label="Additional Approved" value={fmtMoney(costs.additional)} icon={ListChecks} />
            <Kpi testid="client-cost-total" label="Total Facility Spend" value={fmtMoney(costs.total)} tone="text-[#4f8d14]" icon={TrendingUp} />
          </div>

          {noResults && (
            <Card className="mb-6 border-dashed border-slate-200 rounded-2xl"><CardContent className="py-10 text-center" data-testid="client-reports-noresults">
              <ListChecks size={26} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-400">No service activity in the selected range or filters.</p>
            </CardContent></Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <ChartCard title="Created vs Completed" testid="client-chart-throughput">
              <ResponsiveContainer>
                <LineChart data={data?.throughput || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip /><Legend />
                  <Line type="monotone" dataKey="created" stroke={BLUE} strokeWidth={2} name="Created" />
                  <Line type="monotone" dataKey="completed" stroke={GREEN} strokeWidth={2} name="Completed" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Work Orders by Status" testid="client-chart-status">
              <ResponsiveContainer>
                <BarChart data={statusData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="status" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip />
                  <Bar dataKey="count" fill={NAVY} radius={[4, 4, 0, 0]} cursor="pointer"
                    onClick={(d) => d && drill({ status: d.status })} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Work Orders by Priority" testid="client-chart-priority">
              <ResponsiveContainer>
                <BarChart data={priorityData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="priority" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} cursor="pointer"
                    onClick={(d) => d && drill({ priority: d.priority })}>{priorityData.map((e, i) => <Cell key={i} fill={PRIORITY_COLORS[e.priority] || NAVY} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Activity by Trade" testid="client-chart-trade">
              <ResponsiveContainer>
                <BarChart data={data?.by_trade || []} layout="vertical" margin={{ top: 5, right: 10, left: 40, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="trade_type" tick={{ fontSize: 10 }} width={90} /><Tooltip />
                  <Bar dataKey="count" fill={GREEN} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <Card data-testid="client-table-by-location" className="border-slate-200 shadow-sm rounded-2xl overflow-hidden"><CardContent className="p-0">
            <div className="px-5 pt-4 pb-2"><h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">Activity by Service Location</h3></div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50 border-slate-100">
                <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 pl-5">Location</TableHead>
                <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 text-right pr-5">Work Orders</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(data?.by_location || []).map((l) => (
                  <TableRow key={l.location_id || l.location_name} data-testid={`client-by-location-${l.location_id || 'none'}`}
                    className={`border-slate-100 ${l.location_id ? 'cursor-pointer hover:bg-[#8FC93A]/[0.06]' : ''}`}
                    onClick={() => l.location_id && drill({ location_id: l.location_id, location_name: l.location_name })}>
                    <TableCell className="py-3.5 pl-5 font-medium text-slate-700">{l.location_name}</TableCell>
                    <TableCell className="py-3.5 pr-5 text-right font-bold text-[#0E2A47]">{l.count}</TableCell>
                  </TableRow>
                ))}
                {(data?.by_location || []).length === 0 && <TableRow><TableCell colSpan={2} className="py-10 text-center text-slate-400 text-sm">No activity in range.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </>
      )}
    </div>
  );
};

export default ClientReports;
